import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { api as coursesApi } from '../../../../../../api/courses';
import Loader from '../../../../../../components/loader/Loader';
import SelectModules from '../createCourse/components/SelectModules';
import PermissionGate from '../../../../../../components/PermissionGate';
import toast from 'react-hot-toast';

type SelectedModuleType = {
    id: string,
    name?: string,
    duration?: string,
    points?: number,
    assessmentType?: string,
    type?: string,
}

const CourseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [selectedModules, setSelectedModules] = useState<SelectedModuleType[]>([]);

    const { data: courseData, isLoading, refetch } = useQuery(
        ["get course info", id],
        () => coursesApi.getIndividualCourse(id!),
        {
            cacheTime: 100,
            staleTime: 100,
            refetchOnWindowFocus: true,
            select: (data) => data?.data
        }
    );

    useEffect(() => {
        const normalizedModules = (courseData?.modules || courseData?.courseModules || []).map((moduleItem: any) => {
            const moduleData = moduleItem?.module || moduleItem;
            return {
                id: moduleData?.id,
                name: moduleData?.name,
                duration: moduleData?.duration,
                points: moduleData?.points,
                assessmentType: moduleData?.assessmentType,
                type: moduleData?.type,
            };
        }).filter((moduleItem: SelectedModuleType) => !!moduleItem?.id);

        setSelectedModules(normalizedModules);
    }, [courseData]);

    const assignModuleMutation = useMutation(coursesApi.assignModuleToCourse, {
        onSuccess: (response: any) => {
            toast.success(response?.message || 'Module assigned successfully');
            refetch();
        },
        onError: (error: any) => {
            const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || 'Failed to assign module';
            toast.error(message);
        }
    });

    const assignSingleModule = (module: any, isSelected: boolean) => {
        if (!id || isSelected) return;

        const loadingToast = toast.loading('Assigning module...');
        assignModuleMutation.mutate({
            id,
            data: {
                modules: [{
                    moduleId: module?.id,
                    sorting: selectedModules.length + 1,
                }],
            },
        }, {
            onSuccess: () => {
                toast.dismiss(loadingToast);
                setSelectedModules((previous) => [...previous, {
                    id: module?.id,
                    name: module?.name,
                    duration: module?.duration,
                    points: module?.points,
                    assessmentType: module?.assessmentType,
                    type: module?.type,
                }]);
            },
            onError: () => {
                toast.dismiss(loadingToast);
            }
        });
    };

    const unassignModuleMutation = useMutation(coursesApi.unassignModuleFromCourse, {
        onSuccess: (response: any) => {
            toast.success(response?.message || 'Module unassigned successfully');
            refetch();
        },
        onError: (error: any) => {
            const message = error?.response?.data?.errors?.[0]?.message || error?.response?.data?.message || 'Failed to unassign module';
            toast.error(message);
        }
    });

    const removeModule = (module: any, sorting: number) => {
        if (!id) return;

        const loadingToast = toast.loading('Removing module...');
        unassignModuleMutation.mutate({
            id,
            data: {
                modules: [{
                    moduleId: module?.id,
                    sorting,
                }],
            },
        }, {
            onSuccess: () => {
                toast.dismiss(loadingToast);
                setSelectedModules((previous) => previous.filter((moduleItem) => moduleItem?.id !== module?.id));
            },
            onError: () => {
                toast.dismiss(loadingToast);
            }
        });
    };

    const renderedModules = useMemo(() => {
        return selectedModules.map((moduleItem, index) => ({
            index,
            id: moduleItem?.id,
            name: moduleItem?.name,
            duration: moduleItem?.duration,
            points: moduleItem?.points,
            type: moduleItem?.assessmentType || moduleItem?.type,
        }));
    }, [selectedModules]);

    if (isLoading) return <Loader />;

    return (
        <div className="p-5">
            <button 
                onClick={() => navigate('/admin/course')}
                className="flex items-center text-gray-500 hover:text-gray-700 mb-6 font-semibold"
            >
                <i className="bi bi-chevron-left mr-2" /> Back to Courses
            </button>

            {/* Banner Section */}
            <div className="bg-[#0A3059] rounded-lg text-white p-6 mb-6">
                <div className="flex items-center space-x-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${courseData?.status === 'published' ? 'bg-[#D1F2D1] text-[#00984C]' : 'bg-[#FFE8CC] text-[#FF9100]'}`}>
                        {courseData?.status || 'Unknown'}
                    </span>
                    <span className="text-gray-400">—</span>
                </div>
                <h1 className="text-2xl font-bold">{courseData?.name}</h1>
                
                <div className="bg-white text-black mt-6 rounded-lg p-6 flex justify-between shadow-sm">
                    <div>
                        <p className="text-gray-500 text-sm mb-1">Duration</p>
                        <p className="font-semibold">{courseData?.duration || '0h 0m'}</p>
                    </div>
                    <div>
                        <p className="text-gray-500 text-sm mb-1">Points</p>
                        <p className="font-semibold">{courseData?.points || 0} pts</p>
                    </div>
                    <div>
                        <p className="text-gray-500 text-sm mb-1">Price</p>
                        <p className="font-semibold">{courseData?.price > 0 ? courseData?.price : 'Free'}</p>
                    </div>
                    <div>
                        <p className="text-gray-500 text-sm mb-1">Modules</p>
                        <p className="font-semibold">{courseData?.modules?.length || 0} assigned</p>
                    </div>
                </div>
            </div>

            {/* Description */}
            {courseData?.description && (
                <div className="text-gray-600 mb-8 px-2 text-sm" dangerouslySetInnerHTML={{ __html: courseData?.description }} />
            )}

            {/* Assigned Modules */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                <div className="flex justify-between items-center border-b border-gray-100 pb-4 mb-4">
                    <h3 className="font-semibold text-gray-800">Assigned Modules ({selectedModules?.length || 0})</h3>
                    <PermissionGate slug="course-management.update">
                        <button
                            onClick={() => setOpenModal(true)}
                            className="bg-primary-blue text-white px-5 py-2 rounded-xl text-sm font-semibold"
                        >
                            <i className="bi bi-plus mr-2" /> Assign Modules
                        </button>
                    </PermissionGate>
                </div>
                
                <div className="space-y-4">
                    {renderedModules?.map((moduleItem: any) => (
                        <div key={moduleItem?.id} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                            <div className="flex items-center space-x-4">
                                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm">
                                    {moduleItem?.index + 1}
                                </span>
                                <div>
                                    <h4 className="font-semibold text-gray-800">{moduleItem?.name}</h4>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                            moduleItem?.type === 'video' ? 'bg-purple-100 text-purple-600' : 'bg-teal-100 text-teal-600'
                                        }`}>
                                            {moduleItem?.type || 'Content'}
                                        </span>
                                        <span className="text-xs text-gray-500">{moduleItem?.duration || '00:00'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-6">
                                <span className="text-sm text-gray-500 font-semibold">{moduleItem?.points || 0} pts</span>
                                <PermissionGate slug="course-management.update">
                                    <button
                                        type="button"
                                        onClick={() => removeModule(moduleItem, moduleItem?.index + 1)}
                                        className="text-red-400 text-xl"
                                        title="Remove Module"
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                </PermissionGate>
                            </div>
                        </div>
                    ))}
                    {selectedModules?.length === 0 && (
                        <p className="text-center text-gray-500 py-4">No modules assigned yet.</p>
                    )}
                </div>
            </div>

            <PermissionGate slug="course-management.update">
                <SelectModules
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    selectedModule={selectedModules as any}
                    setSelectedModules={setSelectedModules as any}
                    onSelectModule={assignSingleModule}
                />
            </PermissionGate>
        </div>
    );
};

export default CourseDetail;
